import HorizontalLine from '../ui/HorizontalLine';
import StyledFormSection from '../styled/StyledFormSection';
import MarginRateStyledInput from '../styled/MarginRateStyledInput';
import StyledSelect from '../styled/StyledSelect';
import { COUNTRIES, CURRENCIES } from '../../../../shared/CountriesData';
import { Label } from '../ui/label';
import { BENEFIT_MARGIN_RATE_LABEL } from '@/constants/marginRateLabels';
import StyledTranslatableInput from '../styled/StyledTranslatableInput';
import StyledTranslatableTextarea from '../styled/StyledTranslatableTextarea';
import TranslateIcon from '../translation/TranslateIcon';
import TarifList from '../tarifs/TarifList';
import { BenefitType } from '@/types/benefits/benefitTypes';
import { Control, useFormContext } from 'react-hook-form';
import { FlightFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { SupportedLocale } from '@/types/supported-locale.type';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { useChangeLabelValueWithSecondaryValue } from '@/hooks/useChangeLabelValueWithSecondaryValue';
import { useState } from 'react';
import { useGetBenefitsCities } from '@/hooks/useBenefits';
import { useGetFlightAirports } from '@/hooks/useFlight';
import { useWatch } from 'react-hook-form';

interface FlightConfigurationProps {
  control: Control<FlightFormDataWithTranslations>;
  suppliers?: {
    id: string;
    name: string;
    currency: string;
  }[];
  language: SupportedLocale;
  isEditEnabled: boolean;
  translateFields: (
    fields?: Array<'benefit.name' | 'shortDescription' | 'longDescription'>,
  ) => Promise<void>;
  isTranslating: boolean;
  errorsLength: number;
}

const FlightConfiguration = ({
  control,
  suppliers,
  isEditEnabled,
  language,
  translateFields,
  isTranslating,
  errorsLength,
}: FlightConfigurationProps) => {
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const { setValue } = useFormContext<FlightFormDataWithTranslations>();

  const selectedSourceCountry = useWatch({ control, name: 'sourceCountry' });
  const selectedDestinationCountry = useWatch({
    control,
    name: 'destinationCountry',
  });

  const { data: sourceCities } = useGetBenefitsCities({
    country: selectedSourceCountry?.value,
  });
  const { data: destinationCities } = useGetBenefitsCities({
    country: selectedDestinationCountry?.value,
  });
  const { data: sourceAirports } = useGetFlightAirports(
    selectedSourceCountry?.value,
  );
  const { data: destinationAirports } = useGetFlightAirports(
    selectedDestinationCountry?.value,
  );

  const apiSourceCityOptions = sourceCities
    ? sourceCities.map(({ city }) => ({ label: city, value: city }))
    : [];
  const apiDestinationCityOptions = destinationCities
    ? destinationCities.map(({ city }) => ({ label: city, value: city }))
    : [];
  const apiSourceAirportOptions = sourceAirports
    ? sourceAirports.map(({ airport }) => ({
        label: airport,
        value: airport,
      }))
    : [];
  const apiDestinationAirportOptions = destinationAirports
    ? destinationAirports.map(({ airport }) => ({
        label: airport,
        value: airport,
      }))
    : [];

  const [customSourceCityOptions, setCustomSourceCityOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [customDestinationCityOptions, setCustomDestinationCityOptions] =
    useState<Array<{ value: string; label: string }>>([]);
  const [customSourceAirportOptions, setCustomSourceAirportOptions] = useState<
    Array<{ value: string; label: string }>
  >([]);
  const [customDestinationAirportOptions, setCustomDestinationAirportOptions] =
    useState<Array<{ value: string; label: string }>>([]);

  const sourceCityOptions = [
    ...apiSourceCityOptions,
    ...customSourceCityOptions,
  ];
  const destinationCityOptions = [
    ...apiDestinationCityOptions,
    ...customDestinationCityOptions,
  ];
  const sourceAirportOptions = [
    ...apiSourceAirportOptions,
    ...customSourceAirportOptions,
  ];
  const destinationAirportOptions = [
    ...apiDestinationAirportOptions,
    ...customDestinationAirportOptions,
  ];

  const changeSupplierWithCurrency = useChangeLabelValueWithSecondaryValue({
    control,
    primaryName: 'supplier',
    secondaryName: 'currency',
    retrieveSecondary: (primaryValue) => {
      const supplier = suppliers?.find(
        (supplier) => supplier.id === primaryValue.value,
      );
      const currency = CURRENCIES.find(
        (currency) => currency.value === supplier?.currency,
      );
      return currency;
    },
  });

  const suppliersOptions =
    suppliers?.map(({ id, name }) => ({ value: id, label: name })) ?? [];

  return (
    <>
      <StyledFormSection
        sectionTitle={t('forms.sections.generalInfo')}
        icon={
          <TranslateIcon
            translateFields={() => translateFields(['benefit.name'])}
            isTranslating={isTranslating}
            language={language}
            hidden={!isEditEnabled}
          />
        }
      >
        {isEditEnabled && errorsLength > 0 && (
          <Label className="text-error-500">
            {t('common.checkRequiredFields')}
          </Label>
        )}
        <StyledTranslatableInput
          control={control}
          name="benefit.name"
          originalName="name"
          setValue={setValue}
          language={language}
          title={t('flights.flightName')}
          placeholder={t('flights.flightNamePlaceholder')}
          disabled={!isEditEnabled}
        />
        <div className="flex flex-col md:flex-row gap-4">
          <StyledSelect
            options={suppliersOptions}
            control={control}
            name="supplier"
            title={t('forms.labels.supplier') + ' *'}
            placeholder="Ex: Your Private Trip"
            isClearable
            disabled={!isEditEnabled}
            onChange={changeSupplierWithCurrency}
          />
          <StyledSelect
            options={CURRENCIES}
            control={control}
            name="currency"
            title={t('forms.labels.currency') + ' *'}
            placeholder={t('forms.placeholders.selectCurrency')}
            disabled={!isEditEnabled}
          />
        </div>
        <MarginRateStyledInput
          control={control}
          name="marginRate"
          title={getTranslatedConst(BENEFIT_MARGIN_RATE_LABEL)}
          placeholder={t('forms.placeholders.addRate')}
          disabled={!isEditEnabled}
        />
        {isEditEnabled && (
          <Label className="text-gray-700">
            {t('common.requiredFieldsNote')}
          </Label>
        )}
      </StyledFormSection>
      <HorizontalLine />
      <StyledFormSection sectionTitle={t('flights.destinations')}>
        <Label>{t('flights.departureCountry')} *</Label>
        <div className="flex flex-col w-full">
          <div className="flex flex-col md:flex-row gap-4">
            <StyledSelect
              options={COUNTRIES}
              control={control}
              name="sourceCountry"
              placeholder={t('flights.country')}
              disabled={!isEditEnabled}
            />
            <StyledSelect
              options={sourceCityOptions}
              control={control}
              name="sourceCity"
              placeholder={t('flights.city')}
              creatable
              onCreateOption={(option) =>
                setCustomSourceCityOptions((prev) => [
                  ...prev,
                  { label: option, value: option },
                ])
              }
              disabled={!isEditEnabled}
            />
          </div>
          <StyledSelect
            options={sourceAirportOptions}
            control={control}
            name="sourceAirport"
            placeholder={t('flights.airport')}
            creatable
            onCreateOption={(option) =>
              setCustomSourceAirportOptions((prev) => [
                ...prev,
                { label: option, value: option },
              ])
            }
            disabled={!isEditEnabled}
          />
        </div>
        <Label>{t('flights.arrivalCountry')} *</Label>
        <div className="flex flex-col w-full">
          <div className="flex flex-col md:flex-row gap-4">
            <StyledSelect
              options={COUNTRIES}
              control={control}
              name="destinationCountry"
              placeholder={t('flights.country')}
              disabled={!isEditEnabled}
            />
            <StyledSelect
              options={destinationCityOptions}
              control={control}
              name="destinationCity"
              placeholder={t('flights.city')}
              creatable
              onCreateOption={(option) =>
                setCustomDestinationCityOptions((prev) => [
                  ...prev,
                  { label: option, value: option },
                ])
              }
              disabled={!isEditEnabled}
            />
          </div>
          <StyledSelect
            options={destinationAirportOptions}
            control={control}
            name="destinationAirport"
            placeholder={t('flights.airport')}
            creatable
            onCreateOption={(option) =>
              setCustomDestinationAirportOptions((prev) => [
                ...prev,
                { label: option, value: option },
              ])
            }
            disabled={!isEditEnabled}
          />
        </div>
      </StyledFormSection>
      <HorizontalLine />
      <StyledFormSection
        sectionTitle={t('forms.sections.description')}
        icon={
          <TranslateIcon
            translateFields={() =>
              translateFields(['shortDescription', 'longDescription'])
            }
            isTranslating={isTranslating}
            language={language}
            hidden={!isEditEnabled}
          />
        }
      >
        <StyledTranslatableInput
          control={control}
          name="shortDescription"
          originalName="shortDescription"
          setValue={setValue}
          language={language}
          title={t('flights.shortDescription')}
          placeholder={t('flights.shortDescriptionPlaceholder')}
          disabled={!isEditEnabled}
        />
        <StyledTranslatableTextarea
          control={control}
          name="longDescription"
          originalName="longDescription"
          setValue={setValue}
          language={language}
          title={t('flights.longDescription')}
          placeholder={t('flights.longDescriptionPlaceholder')}
          rows={3}
          disabled={!isEditEnabled}
        />
      </StyledFormSection>
      <HorizontalLine />
      <TarifList
        control={control}
        benefitType={BenefitType.VOL}
        isViewMode={!isEditEnabled}
      />
    </>
  );
};

export default FlightConfiguration;
